import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { deviceRegistry, RegisteredDevice } from '../services/deviceRegistry';
import { triggerHaptic } from '../services/purrSynthesizer';
import { Usb, Wifi, Bluetooth, Zap, X, Sliders, Bot, Sparkles } from 'lucide-react';

export const AutoDetectBanner: React.FC = () => {
  const { setActiveTab } = useApp();
  const [detected, setDetected] = useState<RegisteredDevice | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const unsubscribe = deviceRegistry.onDeviceDetected((device) => {
      setDetected(device);
      setVisible(true);
      triggerHaptic([20, 30, 20]);

      // Auto dismiss after 14 seconds if untouched
      const timer = setTimeout(() => {
        setVisible(false);
      }, 14000);

      return () => clearTimeout(timer);
    });

    return () => unsubscribe();
  }, []);

  if (!visible || !detected) return null;

  const getTransportIcon = () => {
    switch (detected.transport) {
      case 'usb':
        return <Usb className="w-4 h-4 text-emerald-400" />;
      case 'wifi':
        return <Wifi className="w-4 h-4 text-sky-400" />;
      case 'bluetooth':
        return <Bluetooth className="w-4 h-4 text-blue-400" />;
      case 'native':
      default:
        return <Zap className="w-4 h-4 text-amber-400" />;
    }
  };

  const handleOpenDeck = () => {
    triggerHaptic(25);
    setVisible(false);
    setActiveTab('diagnostics');
  };

  const handleAskFissal = () => {
    triggerHaptic(25);
    setVisible(false);
    setActiveTab('diagnostics');
  };

  return (
    <div className="relative z-40 px-3 pt-2 pb-1 animate-in slide-in-from-top duration-300">
      <div className="rounded-2xl bg-gradient-to-r from-amber-950/90 via-slate-900/95 to-slate-950/95 border border-fissal-gold/50 shadow-quantum-glow p-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-xl bg-amber-500/20 border border-fissal-gold/40 flex items-center justify-center shrink-0 shadow-quantum-glow">
            {getTransportIcon()}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-amber-500/20 text-fissal-gold font-bold uppercase tracking-wider">
                AUTO-DETECT
              </span>
              <h4 className="text-xs font-bold text-white truncate font-cinzel">
                {detected.name}
              </h4>
            </div>
            <p className="text-[10px] text-slate-300 truncate font-mono">
              {detected.details}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={handleOpenDeck}
            className="px-2.5 py-1.5 rounded-xl bg-fissal-gold hover:bg-amber-400 text-slate-950 text-[11px] font-bold shadow-quantum-glow flex items-center gap-1 active:scale-95 transition-all"
            title="Open specialized tactile controller deck"
          >
            <Sliders className="w-3 h-3" />
            <span>Open Deck</span>
          </button>

          <button
            onClick={handleAskFissal}
            className="px-2 py-1.5 rounded-xl bg-white/10 hover:bg-white/15 text-slate-200 text-[11px] font-medium flex items-center gap-1 transition-all"
            title="Co-pilot with Fissal"
          >
            <Bot className="w-3 h-3 text-fissal-gold" />
            <span className="hidden xs:inline">Co-Pilot</span>
          </button>

          <button
            onClick={() => setVisible(false)}
            className="p-1 rounded-lg text-slate-400 hover:text-white transition-all ml-0.5"
            title="Dismiss notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
